import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Camera, RotateCcw, SwitchCamera, AlertTriangle } from "lucide-react";

/**
 * CameraCapture — opens user's webcam (preferring back camera on mobile)
 * and captures a still frame to base64 (data URL).
 *
 * Props:
 *   onCapture(dataUrl) — required
 *   onClear() — optional, called on retake
 *   captured — current data URL (controls preview vs. live)
 *   facingMode — "user" | "environment" (default "environment")
 *   showFlip — show front/back flip button (default false; enable only for selfies)
 *   testIdPrefix — prefix for data-testid attrs
 *   imageMaxWidth — max width to downscale to (default 1280)
 *   imageQuality — jpeg quality (default 0.85)
 *   overlayDateTime — burn current IST date+time onto bottom-left of the image
 */
export function CameraCapture({
  onCapture,
  onClear,
  captured,
  facingMode: initialFacing = "environment",
  showFlip = false,
  testIdPrefix = "camera",
  helperText,
  imageMaxWidth = 1280,
  imageQuality = 0.85,
  overlayDateTime = false,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState(initialFacing);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      setError("");
      setReady(false);
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
      } catch (e) {
        setError(e?.message || "Unable to access camera. Please grant camera permission.");
      }
    }
    if (!captured) start();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode, captured]);

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const sw = v.videoWidth || 720;
    const sh = v.videoHeight || 540;
    const scale = Math.min(1, imageMaxWidth / sw);
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);
    const cv = document.createElement("canvas");
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);

    if (overlayDateTime) {
      // Burn IST date + time at bottom-center of the captured frame.
      // We render the stamp inside a full-width semi-transparent band so
      // that downstream renderers using `object-cover` (which crops the
      // sides) still keep the stamp visible.
      const fmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const tfmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      const now = new Date();
      const stamp = `${fmt.format(now)}  ${tfmt.format(now)}  IST`;

      const fontSize = Math.max(18, Math.round(h * 0.045));
      const padY = Math.round(fontSize * 0.45);
      ctx.font = `700 ${fontSize}px Arial, sans-serif`;
      ctx.textBaseline = "alphabetic";

      // Full-width band along the bottom edge so center-cropping never clips
      // the timestamp text.
      const boxH = fontSize + padY * 2;
      const boxX = 0;
      const boxY = h - boxH;
      const boxW = w;

      // Semi-transparent black band
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(boxX, boxY, boxW, boxH);

      // Centered text with shadow for high legibility
      ctx.shadowColor = "rgba(0,0,0,0.9)";
      ctx.shadowBlur = 2;
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.fillText(stamp, w / 2, boxY + boxH - padY);
      ctx.textAlign = "start";
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
    }

    const dataUrl = cv.toDataURL("image/jpeg", imageQuality);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    onCapture?.(dataUrl);
  };

  return (
    <div className="flex flex-col gap-3" data-testid={`${testIdPrefix}-root`}>
      <div
        className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border bg-muted"
        data-testid={`${testIdPrefix}-preview`}
      >
        {captured ? (
          <img src={captured} alt="capture" className="absolute inset-0 h-full w-full object-cover" />
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <Alert variant="destructive" data-testid={`${testIdPrefix}-permission-error`}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Camera unavailable</AlertTitle>
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          </div>
        ) : !ready ? (
          <Skeleton className="absolute inset-0" data-testid={`${testIdPrefix}-loading-state`} />
        ) : null}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className={`h-full w-full object-cover ${captured || error ? "hidden" : ""}`}
        />
        {/* framing guide */}
        {!captured && !error && (
          <div className="pointer-events-none absolute inset-6 rounded-xl border-2 border-white/60 mix-blend-difference" />
        )}
      </div>
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      <div className="grid grid-cols-2 gap-3">
        {captured ? (
          <>
            <Button
              variant="outline"
              onClick={() => onClear?.()}
              data-testid={`${testIdPrefix}-retake-button`}
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Retake
            </Button>
            <Button disabled className="opacity-60">Captured</Button>
          </>
        ) : (
          <>
            {showFlip ? (
              <Button
                variant="outline"
                onClick={() => setFacingMode((f) => (f === "user" ? "environment" : "user"))}
                data-testid={`${testIdPrefix}-flip-button`}
              >
                <SwitchCamera className="mr-2 h-4 w-4" /> Flip
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={snap} disabled={!ready} data-testid={`${testIdPrefix}-capture-button`}>
              <Camera className="mr-2 h-4 w-4" /> Capture
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** UploadFallback — for environments without camera (testing). Accepts a file. */
export function PhotoFileInput({ onCapture, label = "Upload photo", testId = "photo-file-input" }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-2">
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={testId}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const fr = new FileReader();
          fr.onload = () => onCapture?.(fr.result);
          fr.readAsDataURL(f);
        }}
      />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()}>
        {label}
      </Button>
    </div>
  );
}
